import {ActionTree, GetterTree, MutationTree} from 'vuex'
import {TasksState, Task, Room, InventoryItem} from '@/store/types'
import axios from 'axios'
import moment from 'moment'
import _ from 'lodash'
import SurveyModel from '@/classes/SurveyModel'


type TasksGetter = GetterTree<TasksState, any>
type TasksMutation = MutationTree<TasksState>
type TasksAction = ActionTree<TasksState, any>

const state: TasksState = {
  taskList: [],
  task: null,
  surveyInProgress: false,
  taskCompleted: null,
  taskCanceled: null,
}

const getters: TasksGetter = {
  taskList: (state) => state.taskList,
  taskListByDates: (state) => {
    const list: any[] = []
    state.taskList.forEach((task) => {
      const date = task.date ? moment(task.date).format('MM/DD/YYYY') : ''
      let item = list.find((i) => i.date === date)
      if (item === undefined) {
        item = {date, tasks: []}
        list.push(item)
      }
      item.tasks.push(task)
    })
    return _.orderBy(list, ['date'], ['asc'])
  },
  task: (state) => state.task,
  taskWithoutDateList: (state) => {
    return state.taskList.filter((t) => t.date === null && t.status !== SurveyModel.STATUS.CANCELED)
  },
  taskPendingList: (state) => {
    return state.taskList.filter((t) => t.date !== null && t.status === SurveyModel.STATUS.PENDING)
  },
  taskCompletedList: (state) => {
    return state.taskList.filter((t) => t.status === SurveyModel.STATUS.COMPLETED)
  },
  taskCanceledList: (state) => {
    return state.taskList.filter((t) => t.status === SurveyModel.STATUS.CANCELED)
  },
  surveyInProgress: (state) => {
    return state.surveyInProgress
  },
  taskCompleted: (state) => state.taskCompleted,
  taskCanceled: (state) => state.taskCanceled,
}

const mutations: TasksMutation = {
  setTaskList: (state, taskList) => {
    state.taskList = taskList
  },
  setTask: (state, task) => {
    state.task = Object.assign({}, task, {lead_location: undefined})
  },
  syncTaskList: (state, task) => {
    state.taskList = state.taskList.map((t) => {
      if (t.id === task.id) {
        return task
      }
      return t
    })
  },
  unsetTask: (state) => {
    state.task = null
  },
  setCompletedTask: (state, payload) => {
    return state.taskCompleted = payload
  },
  setCanceledTask: (state, payload) => {
    const canceledTask = state.taskList.find((t) => t.id === payload)
    if (canceledTask !== undefined) {
      canceledTask.status = SurveyModel.STATUS.CANCELED
      return state.taskCanceled = canceledTask
    }
  },
  unsetCompletedTask: (state) => {
    state.taskCompleted = null
  },
  unsetCanceledTask: (state) => {
    state.taskCanceled = null
  },
  setSurveyInProgress: (state, payload: boolean) => {
    state.surveyInProgress = payload
  },
  syncTaskInventory: (state, {id, inventory}) => {
    const task = state.taskList.find((t: Task) => t.id === id)
    if (task !== undefined && task.lead_location !== undefined) {
      task.lead_location.rooms.forEach((room: Room) => {
        room.inventory = inventory.filter((item: InventoryItem) => item.lead_room_id === room.id)
      })

      state.taskList = state.taskList.map((t: Task) => {
        if (t.id === task.id) {
          return task
        }
        return t
      })
    }
  },
  syncTaskPhotos: (state, {id, photos}) => {
    const task = state.taskList.find((t: Task) => t.id === id)
    if (task !== undefined) {
      task.photos = photos

      state.taskList = state.taskList.map((t: Task) => {
        if (t.id === task.id) {
          return task
        }
        return t
      })
    }
  },
}

const actions: TasksAction = {
  getTaskList: ({commit}, params = []) => {
    params = [...params, ['include=lead_location.lead.company,lead_location.lead.user']].join('&')
    return new Promise((resolve, reject) => {
      axios.get(`surveys?${params}`).then((response) => {
        resolve(response.data.data)
      }).catch((error) => {
        // TODO error handling
        reject(error)
      })
    })
  },
  getTask: ({commit, dispatch}, payload) => {
    const include = 'include=lead_location.lead.company,lead_location.lead.user,' +
      'lead_location.rooms,lead_location.services'
    return new Promise((resolve, reject) => {
      axios.get(`surveys/${payload}?${include}`).then((response) => {
        dispatch('destructureTask', response.data.data)
        resolve(response.data.data)
      }).catch((error) => {
        // TODO error handling
        reject(error)
      })
    })
  },
  setActiveTaskById: ({commit, dispatch, state}, payload) => {
    let task: Task|null|undefined = state.taskList.find((t) => t.id === parseInt(payload, 10))
    if (task === undefined) {
      task = null
      commit('unsetTask')
    }
    dispatch('destructureTask', task)
  },
  destructureTask: ({commit, dispatch, state}, payload) => {
    const task: Task = {
      id: payload.id,
      user_id: payload.user_id,
      status: payload.status,
      date: payload.date,
      description: payload.description,
      time: payload.time,
      accepted_at: payload.accepted_at,
      started_at: payload.started_at,
      completed_at: payload.completed_at,
      canceled_at: payload.canceled_at,
    }

    commit('setTask', task)
    commit('setLocation', payload.lead_location)
    commit('setLead', payload.lead_location.lead)
    commit('setRooms', payload.lead_location.rooms)
    commit('setInventoryFromRooms', payload.lead_location.rooms)
    commit('setComments', payload.comments)
    commit('setPhotos', payload.photos)
    commit('setServices', payload.lead_location.services)
    dispatch('syncServices')
  },
  updateTask: ({commit, dispatch}, payload) => {
    commit('setLoader', true)
    const include = 'include=lead_location.lead.company,lead_location.lead.user,' +
      'lead_location.rooms,lead_location.services'
    return new Promise((resolve, reject) => {
      axios.put(`surveys/${payload.id}?${include}`, payload).then((response) => {
        dispatch('destructureTask', response.data.data)
        commit('setLoader', false)
        resolve(response.data.data)
      }).catch((error) => {
        reject(error)
      })
    })
  },
  focusSearch: () => {
    const search = document.getElementById('search-item')
    if (search) {
      search.focus()
    }
  },
  syncTaskInventory: ({commit, rootState}) => {
    commit('syncTaskInventory', {id: rootState.tasks.task.id, inventory: rootState.inventory.inventory})
  },
  syncTaskPhotos: ({commit, rootState}) => {
    commit('syncTaskPhotos', {id: rootState.tasks.task.id, photos: rootState.photos.photos})
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}
